import { mapWithConcurrency } from '../../../shared/asyncPool.js';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';

/** One row of `get_event_totals_batch`: subtotal after discounts, before tax. */
export type EventTotalRow = {
  event_id: string;
  amount_post_discount: number | string | null;
};

// The RPC's cost grows with the lines it reads, and the authenticated role stops
// a statement at 8 s. The staff gate is checked once per bounded call. Callers that hand it every event on a venue or calendar
// window (3,876 ids were seen in one call) instead send bounded batches,
// a few at a time.
export const EVENT_TOTALS_BATCH_SIZE = 250;
const EVENT_TOTALS_CONCURRENCY = 3;

/**
 * Fetch `get_event_totals_batch` for any number of events in bounded batches.
 * Rows from successful batches are always returned; `error` is the first batch
 * failure, so callers keep their own all-or-nothing or best-effort handling.
 */
export async function fetchEventTotalsBatched(
  supabase: SupabaseClient,
  eventIds: readonly string[],
): Promise<{ data: EventTotalRow[]; error: PostgrestError | null }> {
  const uniqueIds = Array.from(new Set(eventIds.filter(Boolean)));
  const batches: string[][] = [];
  for (let start = 0; start < uniqueIds.length; start += EVENT_TOTALS_BATCH_SIZE) {
    batches.push(uniqueIds.slice(start, start + EVENT_TOTALS_BATCH_SIZE));
  }

  const rows: EventTotalRow[] = [];
  let firstError: PostgrestError | null = null;
  await mapWithConcurrency(batches, EVENT_TOTALS_CONCURRENCY, async (batch: string[]) => {
    const { data, error } = await supabase.rpc('get_internal_event_totals_batch', { _event_ids: batch });
    if (error) {
      firstError ??= error;
      return;
    }
    if (Array.isArray(data)) rows.push(...(data as EventTotalRow[]));
  });

  return { data: rows, error: firstError };
}
